import { toast } from 'qw-ui-lib-v2';
import update from 'immutability-helper';
import _ from 'lodash';
import { format, getUnixTime, parseISO, isValid } from 'date-fns';
import I18n from '../../config/i18n';
import { xlsxFileParser } from '../common/utils/xlsxFileParser';
import * as enums from '../common/utils/enums';
import qwPartners from '../common/clients/qwPartners';
import { handleAppError, showAppBusy, showAppError } from './app';
import { loadWorkOrder } from './workOrder';
import { loadWorkOrders } from './workOrders';

const initialState = {
  shipments: [],
  selectedShipments: [],
  sortColumn: enums.ShippingTabColumns.SHIP_BY,
  sortByAsc: true,
  expanded: false,
  current: null,
  errors: [],
  loading: false,
  closeRightPane: false,
};

const RECEIVE = 'shipments/RECEIVE';
const UPDATE = 'shipments/UPDATE';
const APPLY_SORT = 'shipments/APPLY_SORT';
const SET_SHIPMENT_ERRORS = 'shipments/SET_SHIPMENT_ERRORS';
const RESET_SHIPMENT_ERRORS = 'shipments/RESET_SHIPMENT_ERRORS';
const RESET_SHIPMENTS = 'shipments/RESET_SHIPMENTS';
const RECEIVE_SHIPMENT_INFORMATION = 'shipments/RECEIVE_SHIPMENTS_INFORMATION';
const RESET_SHIPMENTS_SORT_INFO = 'shipments/RESET_SHIPMENTS_SORT_INFO';
const UPDATE_SELECTED_SHIPMENTS = 'shipments/UPDATE_SELECTED_SHIPMENTS';
const LOADING = 'shipments/LOADING';
const CLOSE_RIGHT_PANE = 'shipments/CLOSE_RIGHT_PANE';

const SAVE_FIELDS = [
  'id',
  'status',
  'dispatchedOn',
  'shippingDetails',
  'returnDetails',
  'price',
  'deliveryMode',
];

export const loadShipments = (workOrderId) => {
  return (dispatch, getState) => {
    const { sortColumn, sortByAsc } = getState().shipments;

    dispatch(resetShipments());
    dispatch(setShipmentLoading(true));
    dispatch(showAppBusy(true));
    return qwPartners
      .get(`/v1/work-orders/${workOrderId}/shipments`)
      .then((response) => response.data)
      .then((shipments) => dispatch(receiveShipments(shipments)))
      .then(() => dispatch(sort(sortColumn, sortByAsc)))
      .then(() => dispatch(showAppBusy(false)))
      .then(() => dispatch(setShipmentLoading(false)))
      .catch((error) => {
        dispatch(handleAppError(error, I18n.t('errorWhileFetchingShipments')));
        dispatch(showAppBusy(false));
        dispatch(setShipmentLoading(false));
      });
  };
};

export const updateSelectedShipments = (shipments) => {
  return { type: UPDATE_SELECTED_SHIPMENTS, payload: shipments };
};

export const receiveShipments = (shipments) => {
  return { type: RECEIVE, payload: shipments };
};

export const updateShipment = (index, shipment) => {
  return (dispatch) => {
    return dispatch({ type: UPDATE, payload: shipment, meta: index });
  };
};

export const sort = (column, asc) => {
  return (dispatch, getState) => {
    const shippingTypes = getState().shippingTypes;
    dispatch(setShipmentLoading(true));
    dispatch(applySort(column, asc, shippingTypes));
  };
};

export const applySort = (column, asc, shippingTypes) => {
  return { type: APPLY_SORT, meta: { column, asc, shippingTypes } };
};

export const resetShipments = () => {
  return { type: RESET_SHIPMENTS };
};

export const closeShipmentForm = (payload) => {
  return { type: CLOSE_RIGHT_PANE, payload: payload };
};

export const setShipmentErrors = (errors) => {
  return { type: SET_SHIPMENT_ERRORS, payload: errors };
};

export const setShipmentLoading = (loading) => {
  return { type: LOADING, payload: loading };
};

export const resetShipmentErrors = () => {
  return { type: RESET_SHIPMENT_ERRORS };
};

export const resetShipmentsSortInfo = () => {
  return { type: RESET_SHIPMENTS_SORT_INFO };
};

export const saveShipments = (workOrderId, shipments) => {
  return (dispatch, getState) => {
    let hasErrors = false;
    const errors = [];

    shipments.forEach((shipment) => {
      const { shippingDetails } = shipment;

      if (
        shipment.status === enums.ShippingStatus.DISPATCHED &&
        _.isEmpty(shippingDetails.reference)
      ) {
        hasErrors = true;

        errors.push({
          id: shipment.id,
          errors: {
            reference: true,
          },
        });
      }
    });

    if (hasErrors) {
      return dispatch(setShipmentErrors(errors));
    } else {
      dispatch(resetShipmentErrors());
      dispatch(showAppBusy(true));
      dispatch(setShipmentLoading(true));
      dispatch(closeShipmentForm(true));
      const data = shipments.map((shipment) => {
        if (shipment.status === enums.ShippingStatus.DISPATCHED) {
          const address = getAddress(shipment);
          shipment = update(shipment, {
            shippingDetails: { address: { $set: address } },
          });
        }
        return _.pick(shipment, SAVE_FIELDS);
      });

      return qwPartners
        .put(`/v1/work-orders/${workOrderId}/shipments`, data)
        .then(() =>
          toast.add({
            message: I18n.t('shippingDetailsUpdated'),
            variant: 'positive',
            placement: 'top-right',
          }),
        )
        .then(() => dispatch(loadShipments(workOrderId)))
        .then(() => dispatch(loadWorkOrders(getState().workOrders.itemsFilter)))
        .then(() => dispatch(loadWorkOrder(workOrderId)))
        .then(() => dispatch(showAppBusy(false)))
        .then(() => dispatch(setShipmentLoading(false)))
        .then(() => dispatch(closeShipmentForm(false)))
        .then(() => dispatch(updateSelectedShipments([])))
        .catch(() => {
          dispatch(showAppBusy(false));
          toast.add({
            message: I18n.t('thereWasAnErrorProcessingYourRequest'),
            variant: 'negative',
            placement: 'top-right',
          });
        });
    }
  };
};

const getAddress = (shipment) => {
  return {
    street: shipment.theatre.street,
    displayLocation: shipment.theatre.displayLocation,
    city: {
      code: shipment.theatre.cityCode,
      name: shipment.theatre.city,
    },
    state: {
      code: shipment.theatre.stateCode,
      name: shipment.theatre.state,
    },
    country: {
      code: shipment.theatre.countryCode,
      name: shipment.theatre.country,
    },
    postalCode: shipment.theatre.postalCode,
    addressType: shipment.theatre.addressType,
  };
};

const filterValidShipmentsFromExcel = (shipmentInformation) => {
  return shipmentInformation.filter(
    (row) =>
      !_.isEmpty(
        row[enums.ShipmentsExcelHeaderColumnNames.SHIPPING_COURIER_TYPE],
      ) &&
      !_.isEmpty(
        row[enums.ShipmentsExcelHeaderColumnNames.SHIPPING_TRACKING_NUMBER],
      ),
  );
};

export const uploadShipmentInformation = (xlsxFile) => {
  return (dispatch, getState) => {
    dispatch(showAppBusy(true));

    xlsxFileParser(xlsxFile)
      .then((shipmentsFromExcel) => {
        const { shipments } = getState().shipments;
        const uniqShipment = _.uniqBy(
          shipmentsFromExcel,
          enums.ShipmentsExcelHeaderColumnNames.WORK_ORDER_NUMBER,
        );

        if (
          uniqShipment.length > 1 ||
          uniqShipment[0][
            enums.ShipmentsExcelHeaderColumnNames.WORK_ORDER_NUMBER
          ] !== shipments[0].workOrderNo
        ) {
          throw new Error(
            I18n.t('shipmentsErrorsUploadInvalidWorkorderMessage'),
          );
        }

        shipmentsFromExcel = shipmentsFromExcel.map((item) => {
          const obj = _.cloneDeep(item);
          if (
            item[enums.ShipmentsExcelHeaderColumnNames.SHIPPING_TRACKING_NUMBER]
          ) {
            obj[
              enums.ShipmentsExcelHeaderColumnNames.SHIPPING_TRACKING_NUMBER
            ] =
              item[
                enums.ShipmentsExcelHeaderColumnNames.SHIPPING_TRACKING_NUMBER
              ].toString();
          }
          if (
            item[enums.ShipmentsExcelHeaderColumnNames.RETURN_TRACKING_NUMBER]
          ) {
            obj[enums.ShipmentsExcelHeaderColumnNames.RETURN_TRACKING_NUMBER] =
              item[
                enums.ShipmentsExcelHeaderColumnNames.RETURN_TRACKING_NUMBER
              ].toString();
          }
          return obj;
        });
        return shipmentsFromExcel;
      })
      .then(filterValidShipmentsFromExcel)
      .then((shipmentsFromExcel) => {
        const shippingTypes = getState().shippingTypes;

        return {
          shipmentsFromExcel: _.map(shipmentsFromExcel, (row) =>
            validateShipmentTypes(shippingTypes, row),
          ),
          shippingTypes: shippingTypes,
        };
      })
      .then((shipmentInformation) => {
        dispatch(showAppBusy(false));
        dispatch(receiveShipmentInformation(shipmentInformation));
      })
      .then(() => {
        const {
          workOrder: { workOrderNo },
          shipments: { shipments },
        } = getState();
        const error = shipments.filter(
          (shipment) => shipment.shippingTypeErrorText,
        );
        if (error.length === 0) {
          dispatch(saveShipments(workOrderNo, shipments));
        } else {
          const theatresError = error.map((shipment, id) => (
            <div key={id}>{shipment.theatre.name}</div>
          ));
          const theatre = <div>{theatresError}</div>;
          toast.add({
            title: 'Invalid Courier Type for the Following Theatres:',
            message: theatre,
            variant: 'negative',
            placement: 'top-right',
          });
        }
      })
      .catch((err) => {
        dispatch(showAppBusy(false));
        const errInfo = {
          title: I18n.t('shipmentsErrorsUploadInvalidWorkorderTitle'),
          message: err.message,
        };

        dispatch(showAppError(errInfo));
      });
  };
};

const _validateShipmentType = (shippingTypes, type, shipment, errorTextKey) => {
  if (!_.isEmpty(type) && !getShippingType(shippingTypes, type)) {
    shipment[errorTextKey] = I18n.t('invalidCourierType');
  } else {
    delete shipment[errorTextKey];
  }
};

const validateShipmentTypes = (shippingTypes, shipment) => {
  let type =
    shipment[enums.ShipmentsExcelHeaderColumnNames.SHIPPING_COURIER_TYPE];
  _validateShipmentType(shippingTypes, type, shipment, 'shippingTypeErrorText');

  type = shipment[enums.ShipmentsExcelHeaderColumnNames.RETURN_COURIER_TYPE];
  _validateShipmentType(shippingTypes, type, shipment, 'returnTypeErrorText');

  return shipment;
};

const getShippingType = (shippingTypes, type) => {
  if (!type) {
    return null;
  }

  return shippingTypes.find((s) => s.name.toLowerCase() === type.toLowerCase());
};

export const receiveShipmentInformation = (shipmentDetails) => {
  return { type: RECEIVE_SHIPMENT_INFORMATION, payload: shipmentDetails };
};

const performPrimarySortForShipments = (shipments, meta) => {
  return _.sortBy(shipments, (shipment) => {
    switch (meta.column) {
      case enums.ShippingTabColumns.ADDRESS: {
        return (
          shipment.theatre.country +
          shipment.theatre.state +
          shipment.theatre.city +
          shipment.theatre.name
        );
      }

      case enums.ShippingTabColumns.SHIPPING_INFO: {
        const shippingType = _.find(
          meta.shippingTypes,
          (shippingType) =>
            shippingType.name ===
            (shipment.shippingDetails ? shipment.shippingDetails.type : ''),
        );

        return shippingType ? shippingType.name : '';
      }

      case enums.ShippingTabColumns.DELIVERY_INSTRUCTIONS:
      case enums.ShippingTabColumns.DELIVER_BY:
      case enums.ShippingTabColumns.SHIP_BY:
      case enums.ShippingTabColumns.SHIPPED: {
        return shipment.ordinal;
      }
    }
  });
};

const performSecondarySortForShipments = (primarySortedShipments, meta) => {
  if (meta.column === enums.ShippingTabColumns.DELIVER_BY) {
    return _.sortBy(primarySortedShipments, (shipment) => {
      const parsedDate = shipment.deliverOn && parseISO(shipment.deliverOn);
      return parsedDate && isValid(parsedDate)
        ? getUnixTime(parsedDate)
        : Number.MAX_SAFE_INTEGER;
    });
  }

  if (meta.column === enums.ShippingTabColumns.SHIP_BY) {
    return _.sortBy(primarySortedShipments, (shipment) => {
      const parsedDate =
        shipment.dispatchDeadline && parseISO(shipment.dispatchDeadline);
      return parsedDate && isValid(parsedDate)
        ? getUnixTime(parsedDate)
        : Number.MAX_SAFE_INTEGER;
    });
  }

  if (meta.column === enums.ShippingTabColumns.SHIPPED) {
    return _.sortBy(primarySortedShipments, (shipment) => {
      return shipment.dispatchedOn
        ? shipment.status === enums.ShippingStatus.PENDING
          ? enums.ShippedSortIndex.PENDING
          : enums.ShippedSortIndex.SHIPPED
        : enums.ShippedSortIndex.NOT_AVAILABLE;
    });
  }

  return primarySortedShipments;
};

const sortShipments = (shipments, meta) => {
  // In stable sorting mechanism, to sort by multiple attributes, we need to sort from least important attribute to
  // most important attribute.
  //
  // https://stackoverflow.com/questions/16426774/underscore-sortby-based-on-multiple-attributes
  //
  // In our case, we need to sort by 'deliver by' (first or most important attribute) and then sort by
  // 'shipment ordinal' (second or least important attribute), which is done by sorting by 'shipment ordinal'
  // attribute first and then by 'deliver by' attribute
  // We need to sort by multiple attributes certain columns for the following reasons
  //
  // 'Deliver By' column
  // *******************
  // As shipment ordinals were not generated properly for old bookings, shipment ordinals in the ascending order
  // will not be mapped correctly with deliver by dates in the ascending order so we need to sort by 'deliver by'
  // first and then by 'shipment ordinal' to make sure the order in which a particular shipment is rendered in the UI is
  // consistent across reloads. This problem will not arise for new bookings where shipment ordinal is properly
  // generated
  //
  // 'Shipped' column
  // *******************
  // This column will have same computed values (i.e enums.ShippedSortIndex.PENDING for pending shipments
  // and enums.ShippedSortIndex.SHIPPED for shipped shipments), so we need to sort by shipped status (pending/shipped)
  // first and then by 'shipment ordinal' for rendering in the UI as explained in 'Deliver by' column section above
  const primarySortedShipments = performPrimarySortForShipments(
    shipments,
    meta,
  );
  const sortedShipments = performSecondarySortForShipments(
    primarySortedShipments,
    meta,
  );

  return meta.asc ? sortedShipments : _.reverse(sortedShipments);
};

const updateShippingInfo = (state, payload, meta) => {
  const newShippingDetails = {
    type: payload.shippingDetails.type,
    slug: payload.shippingDetails.slug,
    reference: null,
    comments: null,
  };

  const updatedShipments = state.shipments.map((shipment, index) => {
    if (index === meta) {
      let updatedShipment = update(shipment, {
        shippingDetails: { $set: payload.shippingDetails },
        shippingTypeErrorText: { $set: null },
      });

      if (!shipment.returnDetails || !shipment.returnDetails.type) {
        updatedShipment = update(updatedShipment, {
          returnDetails: { $set: newShippingDetails },
          returnTypeErrorText: { $set: null },
        });
      }

      return updatedShipment;
    } else {
      if (!shipment.shippingDetails || !shipment.shippingDetails.type) {
        return update(shipment, {
          shippingDetails: { $set: newShippingDetails },
          returnDetails: { $set: newShippingDetails },
        });
      } else {
        return shipment;
      }
    }
  });

  return update(state, { shipments: { $set: updatedShipments } });
};

const updateShippedStatus = (state, payload, meta) => {
  const newState = update(state, {
    shipments: {
      [meta]: { $merge: payload },
    },
  });

  return update(newState, {
    shipments: {
      [meta]: {
        shippingTypeErrorText: { $set: null },
        returnTypeErrorText: { $set: null },
      },
    },
  });
};

const resolveDataBetweenUiAndExcel = (uiData, excelData) => {
  if (!excelData || excelData.trim().length === 0) {
    return uiData;
  }

  return excelData;
};

const resolveCourierTypeFromExcel = (shippingTypes, shippingTypeFromExcel) => {
  return getShippingType(shippingTypes, shippingTypeFromExcel);
};

const updateShipmentsStateFromExcel = (state, payload, shipmentsFromState) => {
  payload.shipmentsFromExcel.forEach((shipmentFromExcel) => {
    const foundShipmentFromState = shipmentsFromState.find(
      (shipmentFromState) =>
        shipmentFromState.ordinal ===
          parseInt(
            shipmentFromExcel[
              enums.ShipmentsExcelHeaderColumnNames.SERIAL_NUMBER
            ],
          ) &&
        shipmentFromState.status !== enums.ShippingStatus.CANCELLED &&
        shipmentFromState.status !==
          enums.ShippingStatus.PENDING_CANCELLATION &&
        shipmentFromState.status !== enums.ShippingStatus.REJECTED,
    );

    if (foundShipmentFromState) {
      if (!foundShipmentFromState.shippingDetails) {
        foundShipmentFromState.shippingDetails = {
          type: null,
          slug: null,
          reference: null,
          comments: null,
        };
      }

      let courierTypeFromExcel = resolveCourierTypeFromExcel(
        payload.shippingTypes,
        shipmentFromExcel[
          enums.ShipmentsExcelHeaderColumnNames.SHIPPING_COURIER_TYPE
        ],
      );
      let courierTypeNameFromExcel =
        courierTypeFromExcel && courierTypeFromExcel.name;
      let courierSlugNameFromExcel =
        courierTypeFromExcel && courierTypeFromExcel.aftership_slug;
      const statusFromShipmentExcel = getShipmentStatus(
        shipmentFromExcel[
          enums.ShipmentsExcelHeaderColumnNames.SHIPPING_STATUS
        ],
      );
      foundShipmentFromState.shippingDetails.type =
        resolveDataBetweenUiAndExcel(
          foundShipmentFromState.shippingDetails.type,
          courierTypeNameFromExcel,
        );

      if (
        foundShipmentFromState.shippingDetails.type === courierTypeNameFromExcel
      ) {
        foundShipmentFromState.shippingDetails.slug = courierSlugNameFromExcel;
      }

      foundShipmentFromState.shippingDetails.reference =
        resolveDataBetweenUiAndExcel(
          foundShipmentFromState.shippingDetails.reference,
          shipmentFromExcel[
            enums.ShipmentsExcelHeaderColumnNames.SHIPPING_TRACKING_NUMBER
          ],
        );
      foundShipmentFromState.shippingDetails.comments =
        resolveDataBetweenUiAndExcel(
          foundShipmentFromState.shippingDetails.comments,
          shipmentFromExcel[
            enums.ShipmentsExcelHeaderColumnNames.SHIPPING_COMMENTS
          ],
        );
      foundShipmentFromState.shippingDetails.address = getAddress(
        foundShipmentFromState,
      );

      if (!foundShipmentFromState.returnDetails) {
        foundShipmentFromState.returnDetails = {
          type: null,
          slug: null,
          reference: null,
          comments: null,
        };
      }

      courierTypeFromExcel = resolveCourierTypeFromExcel(
        payload.shippingTypes,
        shipmentFromExcel[
          enums.ShipmentsExcelHeaderColumnNames.RETURN_COURIER_TYPE
        ],
      );
      courierTypeNameFromExcel =
        courierTypeFromExcel && courierTypeFromExcel.name;
      courierSlugNameFromExcel =
        courierTypeFromExcel && courierTypeFromExcel.aftership_slug;

      foundShipmentFromState.returnDetails.type = resolveDataBetweenUiAndExcel(
        foundShipmentFromState.returnDetails.type,
        courierTypeNameFromExcel,
      );
      if (
        foundShipmentFromState.returnDetails.type === courierTypeNameFromExcel
      ) {
        foundShipmentFromState.returnDetails.slug = courierSlugNameFromExcel;
      }

      foundShipmentFromState.returnDetails.reference =
        resolveDataBetweenUiAndExcel(
          foundShipmentFromState.returnDetails.reference,
          shipmentFromExcel[
            enums.ShipmentsExcelHeaderColumnNames.RETURN_TRACKING_NUMBER
          ],
        );
      foundShipmentFromState.returnDetails.comments =
        resolveDataBetweenUiAndExcel(
          foundShipmentFromState.returnDetails.comments,
          shipmentFromExcel[
            enums.ShipmentsExcelHeaderColumnNames.RETURN_COMMENTS
          ],
        );

      foundShipmentFromState.status = resolveDataBetweenUiAndExcel(
        foundShipmentFromState.status,
        statusFromShipmentExcel,
      );
      if (foundShipmentFromState.status === enums.ShippingStatus.DISPATCHED) {
        foundShipmentFromState.dispatchedOn = format(
          new Date(),
          'yyyy-MM-dd HH:mm:ss.SSSxxx',
        );
      }
      // foundShipmentFromState.status = enums.ShippingStatus.DISPATCHED      need to update the status through checkbox
      foundShipmentFromState.shippingTypeErrorText =
        shipmentFromExcel.shippingTypeErrorText;
      foundShipmentFromState.returnTypeErrorText =
        shipmentFromExcel.returnTypeErrorText;
    }
  });

  return update(state, { shipments: { $set: shipmentsFromState } });
};

function getShipmentStatus(status) {
  const ShipmentExcelStatus = {
    [enums.ShipmentsExcelStatus.PENDING]: enums.ShippingStatus.PENDING,
    [enums.ShipmentsExcelStatus.DISPATCHED]: enums.ShippingStatus.DISPATCHED,
    [enums.ShipmentsExcelStatus.DELIVERED]: enums.ShippingStatus.DELIVERED,
    [enums.ShipmentsExcelStatus.PENDING_CANCELLATION]:
      enums.ShippingStatus.PENDING_CANCELLATION,
    [enums.ShipmentsExcelStatus.CANCELLED]: enums.ShippingStatus.CANCELLED,
    [enums.ShipmentsExcelStatus.REJECTED]: enums.ShippingStatus.REJECTED,
  };
  return ShipmentExcelStatus[status];
}

const reducer = (state = initialState, action) => {
  const { type, payload, meta } = action;

  switch (type) {
    case RECEIVE:
      return Object.assign({}, initialState, {
        shipments: payload,
      });

    case UPDATE: {
      if (payload.shippingDetails || payload.shippingDetails) {
        return updateShippingInfo(state, payload, meta);
      }

      return updateShippedStatus(state, payload, meta);
    }

    case APPLY_SORT: {
      const sortedShipments = sortShipments(state.shipments, meta);

      return update(state, {
        sortColumn: { $set: meta.column },
        sortByAsc: { $set: meta.asc },
        shipments: { $set: sortedShipments },
        loading: { $set: false },
      });
    }
    case SET_SHIPMENT_ERRORS:
      return update(state, { errors: { $set: payload } });

    case LOADING:
      return update(state, { loading: { $set: payload } });

    case RESET_SHIPMENT_ERRORS:
      return update(state, { errors: { $set: [] } });

    case CLOSE_RIGHT_PANE:
      return update(state, { closeRightPane: { $set: payload } });

    case RESET_SHIPMENTS:
      return initialState;

    case RECEIVE_SHIPMENT_INFORMATION: {
      const shipmentsFromState = _.cloneDeep(state.shipments);
      return updateShipmentsStateFromExcel(state, payload, shipmentsFromState);
    }

    case RESET_SHIPMENTS_SORT_INFO:
      return update(state, {
        sortColumn: { $set: initialState.sortColumn },
        sortByAsc: { $set: initialState.sortByAsc },
      });
    case UPDATE_SELECTED_SHIPMENTS:
      return update(state, {
        selectedShipments: { $set: payload },
      });

    default:
      return state;
  }
};

export default reducer;
